import { T } from "../../lib/fc3i18n";
import React, { useEffect, useRef } from "react";

/* ------------------------------------------------------------------ *
 * FemCare 3.0 hero stage (light, Flo/Glow-inspired, our own design).
 * A real FemCare app screen in a 3D phone, a tilted 3D cycle ring
 * (four phase colours) orbiting behind it, and floating UI cards at
 * different depths that react to the pointer. Pure CSS 3D — works on
 * every device, no WebGL. Holds still for prefers-reduced-motion.
 * ------------------------------------------------------------------ */

const getCards = () => [{
  cls: "fc-hcard--next",
  z: 140,
  body: <>
      <span className="fc-hcard-dot" style={{
      background: "#FF5C8A"
    }} />
      <div>
        <p className="fc-hcard-k">{T("Next period")}</p>
        <p className="fc-hcard-v">{T("in 24 days")}</p>
      </div>
    </>
}, {
  cls: "fc-hcard--fertile",
  z: 190,
  body: <>
      <span className="fc-hcard-dot" style={{
      background: "#1FB89A"
    }} />
      <div>
        <p className="fc-hcard-k">{T("Fertile window")}</p>
        <p className="fc-hcard-v">{T("Estimated · Day 10–15")}</p>
      </div>
    </>
}, {
  cls: "fc-hcard--ai",
  z: 230,
  body: <div>
      <p className="fc-hcard-k" style={{
      color: "#6B5BEF"
    }}>{T("FemCare AI · English & हिंदी")}</p>
      <p className="fc-hcard-chat">{T("Hi! Ask me anything about your cycle.")}</p>
    </div>
}, {
  cls: "fc-hcard--log",
  z: 110,
  body: <div>
      <p className="fc-hcard-k">{T("Log today")}</p>
      <div className="flex gap-1.5 mt-1.5" aria-hidden="true">
        {["#FFE1EA", "#EFEBFF", "#E3F8F2", "#FFF1DC"].map((c, i) => <span key={c} className="w-8 h-8 rounded-xl flex items-center justify-center text-[15px]" style={{
        background: c
      }}>
            {["💧", "😊", "🌙", "⚡"][i]}
          </span>)}
      </div>
    </div>
}, {
  cls: "fc-hcard--trend",
  z: 160,
  body: <div>
      <p className="fc-hcard-k">{T("Cycle length trend")}</p>
      <svg viewBox="0 0 120 40" className="w-[120px] h-[40px] mt-1" aria-hidden="true">
        <polyline points="2,26 22,20 42,24 62,14 82,18 102,12 118,16" fill="none" stroke="#6B5BEF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {[[2, 26], [22, 20], [42, 24], [62, 14], [82, 18], [102, 12], [118, 16]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="2.6" fill="#fff" stroke="#6B5BEF" strokeWidth="1.6" />)}
      </svg>
    </div>
}];
export default function HeroStage() {
  const rig = useRef(null);
  const host = useRef(null);
  useEffect(() => {
    const el = rig.current;
    const box = host.current;
    if (!el || !box) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let tx = 0,
      ty = 0,
      cx = 0,
      cy = 0,
      raf = 0,
      visible = true;
    const onMove = e => {
      tx = e.clientX / window.innerWidth * 2 - 1;
      ty = e.clientY / window.innerHeight * 2 - 1;
    };
    const loop = () => {
      raf = 0;
      if (!visible) return;
      cx += (tx - cx) * 0.06;
      cy += (ty - cy) * 0.06;
      const p = Math.min(1, window.scrollY / (window.innerHeight || 800));
      el.style.setProperty("--ry", `${(-12 + cx * 12).toFixed(2)}deg`);
      el.style.setProperty("--rx", `${(4 - cy * 7 + p * 8).toFixed(2)}deg`);
      el.style.setProperty("--lift", `${(-p * 40).toFixed(1)}px`);
      el.style.setProperty("--gx", `${(50 + cx * 30).toFixed(1)}%`);
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("pointermove", onMove, {
      passive: true
    });
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(loop);
    });
    io.observe(box);
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
    };
  }, []);
  return <div ref={host} className="fc-hstage absolute inset-0" aria-label={T("FemCare app preview")}>
      <div className="fc-hblob fc-hblob--a" aria-hidden="true" />
      <div className="fc-hblob fc-hblob--b" aria-hidden="true" />
      <div className="fc-hblob fc-hblob--c" aria-hidden="true" />
      <div ref={rig} className="fc-hrig absolute inset-0">
        {/* 3D cycle ring */}
        <div className="fc-hring" aria-hidden="true">
          <div className="fc-hring-band" />
          <span className="fc-hring-today" />
        </div>
        {/* phone with a real app screen */}
        <div className="fc-hphone">
          <div className="fc-phone-body">
            <div className="fc-phone-screen">
              <img src="/femcare-ui-v2.jpg" alt={T("FemCare app home screen with cycle ring, days until next period and calendar")} loading="eager" decoding="async" draggable="false" />
              <span className="fc-phone-glare" aria-hidden="true" />
            </div>
          </div>
        </div>
        {/* floating UI cards */}
        {getCards().map((c, i) => <div key={c.cls} className={`fc-hcard ${c.cls}`} style={{
        "--z": `${c.z}px`,
        animationDelay: `${0.5 + i * 0.12}s`
      }} aria-hidden="true">
            {c.body}
          </div>)}
      </div>
    </div>;
}